var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SavedCartItemComponent, { GetKeyValue } from './item';
import PalletModal from '@themes/checkout/components/palletmodal';
import { GetCommonSettings } from '@themes/common/data';
import { HasVatDisplay, IsMobile } from '@themes/common/utils';
import ImportAxios from '@themes/common/axios';
import { AddToCart } from '@themes/cart/common';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { createRoot } from 'react-dom/client';
import { FormatMoney } from '@components/Shared/Utils/price';
import ShopifyAjaxApi from '@themes/common/shopifyApi';
function SavedCarts({ share_view, preview, cart, settings: savedCartsSettings, axios }) {
    const { id, title, items, shareable } = cart;
    const settings = GetCommonSettings();
    const { cart_id_text, replace_cart_text, delete_button_text, share_text, copy_link_text, copied_text, delete_confirm_text, title_text, print_text, back_link_text, subtotal_text, } = savedCartsSettings;
    const [products, setProducts] = useState({});
    const [openPallet, setOpenPallet] = useState(false);
    const [openBreakingPallet, setOpenBreakingPallet] = useState(false);
    const [openTipping, setOpenTipping] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentTitle, setCurrentTitle] = useState(title);
    const [currentShareable, setCurrentShareable] = useState(shareable);
    const parent_items = useMemo(() => items.filter((item) => items.filter((f) => GetKeyValue(f.properties, '_parent_id') && GetKeyValue(f.properties, '_parent_id') === GetKeyValue(item.properties, '_uid')).length > 0), [items]);
    const sub_total = useMemo(() => items.reduce((pres, { price, quantity }) => pres + parseFloat(price) * 100 * (HasVatDisplay ? 0.8 : 1) * quantity, 0), [items]);
    const openPalletHandler = useCallback(() => {
        setOpenPallet(true);
    }, []);
    const openBreakingPalletHandler = useCallback(() => {
        setOpenBreakingPallet(true);
    }, []);
    useEffect(() => {
        const fetch = () => __awaiter(this, void 0, void 0, function* () {
            const result = yield axios.get(`${process.env.HOST}/api/product/lists`, {
                params: {
                    ids: parent_items.map((i) => i.product_id).join(','),
                },
            });
            setProducts(result.data.reduce((acc, item) => {
                acc[item.variant_id] = item;
                return acc;
            }, {}));
            setLoading(false);
        });
        fetch();
    }, [axios, parent_items]);
    useEffect(() => {
        globalThis.common_products = products;
    }, [products]);
    useEffect(() => {
        const saveTitleHandler = () => __awaiter(this, void 0, void 0, function* () {
            setLoading('title_change');
            if (!!currentTitle) {
                yield axios.put(`/a/saved-carts/${id}`, {
                    customer_id: globalThis.aaCustomerId,
                    key: globalThis.aaKey,
                    title: currentTitle,
                });
            }
            setLoading(false);
        });
        const debounceHandler = debounce(saveTitleHandler, IsMobile() ? 1500 : 500);
        debounceHandler();
        return () => {
            debounceHandler.cancel();
        };
    }, [id, currentTitle, axios]);
    const replaceCartHandler = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setLoading('production_quantity_limits');
        yield ShopifyAjaxApi.CartClear();
        const items = parent_items.map((item) => {
            const properties = item.properties.reduce((pv, cv) => {
                pv[cv.name] = cv.value;
                return pv;
            }, {});
            return {
                id: item.variant_id,
                quantity: item.quantity,
                properties,
                isUpdate: false,
            };
        });
        yield AddToCart(items, {
            project_name: currentTitle,
        });
        setLoading(false);
        if (!globalThis.sb_processing) {
            window.location.href = `${globalThis.Shopify.routes.root}cart`;
        }
    }), [currentTitle, parent_items]);
    const deleteCartHandler = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const deleteConfirmed = confirm(delete_confirm_text);
        setLoading('remove_shopping_cart');
        if (deleteConfirmed) {
            yield axios.delete(`/a/saved-carts/${id}`, {
                params: {
                    customer_id: globalThis.aaCustomerId,
                    key: globalThis.aaKey,
                },
            });
            window.location.href = '/a/saved-carts';
        }
        setLoading(false);
    }), [delete_confirm_text, axios, id]);
    const linkCopyHandler = useCallback(() => {
        navigator.clipboard.writeText(`${window.location.href}/share`);
        globalThis.alertify.notify(copied_text, 'success', 5, function () { });
    }, [copied_text]);
    const shareCartHandler = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setLoading('share');
        yield axios.put(`/a/saved-carts/${id}`, {
            customer_id: globalThis.aaCustomerId,
            key: globalThis.aaKey,
            shareable: !currentShareable,
        });
        setLoading(false);
        setCurrentShareable(!currentShareable);
        if (!currentShareable) {
            linkCopyHandler();
        }
    }), [axios, id, currentShareable, linkCopyHandler]);
    const printCartHandler = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        globalThis.print();
    }), []);
    const load_icon = (icon, is_loading) => (loading == icon || is_loading ? 'refresh' : icon);
    const disableEdit = share_view || preview;
    const titleDisplay = disableEdit ? (_jsx("h1", { children: currentTitle })) : (_jsx(_Fragment, { children: _jsx("div", Object.assign({ className: 'form-fields-columns' }, { children: _jsxs("div", Object.assign({ className: 'form-field form-field--half' }, { children: [_jsx("input", { type: 'text', className: 'form-field-input form-field-text form-field-filled', id: 'first_name', name: 'customer[first_name]', autoCapitalize: 'words', "aria-label": title_text, value: currentTitle, onChange: (e) => setCurrentTitle(e.target.value) }), _jsxs("label", Object.assign({ className: 'form-field-title', htmlFor: 'first_name' }, { children: [title_text, ' '] })), _jsx("span", Object.assign({ className: clsx('material-icons', {
                            spinner: loading == 'title_change',
                        }), style: {
                            position: 'absolute',
                            right: '10px',
                            top: '12px',
                        } }, { children: load_icon('edit', loading == 'title_change') }))] })) })) }));
    return (_jsxs("div", Object.assign({ className: 'sb-sc--wrapper' }, { children: [_jsx(PalletModal, { openPallet: openPallet, openBreakingPallet: openBreakingPallet, setOpenBreakingPallet: setOpenBreakingPallet, setOpenPallet: setOpenPallet, openTipping: openTipping, setOpenTipping: setOpenTipping }), _jsxs("div", Object.assign({ className: 'sb-sc--tittle-wrapper hide-on-print' }, { children: [_jsx("div", Object.assign({ className: 'sb-sc--backlink-wrapper' }, { children: _jsxs("a", Object.assign({ href: '/a/saved-carts/', title: back_link_text }, { children: [_jsx("div", Object.assign({ className: clsx('material-icons', {
                                        spinner: loading == 'add_shopping_cart',
                                    }) }, { children: load_icon('keyboard_backspace') })), back_link_text] })) })), titleDisplay, _jsx("hr", {}), _jsxs("div", Object.assign({ className: 'sb-sc--subtitle' }, { children: [cart_id_text, ": ", id] }))] })), _jsxs("div", Object.assign({ className: 'sb-sc--content-wrapper' }, { children: [_jsx("div", Object.assign({ className: 'sb-cart-items' }, { children: parent_items.map((item) => (_jsx(SavedCartItemComponent, { item: item, cart: cart, openPalletHandler: openPalletHandler, openBreakingPalletHandler: openBreakingPalletHandler, settings: settings, products: products, savedCartsSettings: savedCartsSettings }, item.id))) })), _jsxs("div", Object.assign({ className: 'sb-sc--subtotal-wrapper' }, { children: [_jsx("div", Object.assign({ className: 'sb-sc--additional-info' }, { children: settings.translations.saved_carts.additional_info })), _jsxs("div", Object.assign({ className: 'sb-sc--subtotal-container' }, { children: [_jsx("div", Object.assign({ className: 'sb-sc--subtotal-title' }, { children: subtotal_text })), _jsx("h3", Object.assign({ className: 'money' }, { children: FormatMoney(sub_total, settings.money_format) }))] }))] }))] })), _jsxs("div", Object.assign({ className: 'sb-sc-button-wrapper hide-on-print' }, { children: [_jsxs("button", Object.assign({ className: clsx('button-primary', {
                            disabled: loading,
                        }), type: 'submit', disabled: !!loading, name: 'checkout', onClick: replaceCartHandler }, { children: [_jsx("div", Object.assign({ className: clsx('material-icons', {
                                    spinner: loading == 'production_quantity_limits',
                                }) }, { children: load_icon('production_quantity_limits') })), ' ', replace_cart_text] })), !disableEdit && (_jsxs(_Fragment, { children: [_jsx("div", { className: 'break' }), _jsxs("button", Object.assign({ className: clsx('button-secondary', {
                                    disabled: loading,
                                }), type: 'submit', disabled: !!loading, name: 'checkout', onClick: deleteCartHandler }, { children: [_jsx("div", Object.assign({ className: clsx('material-icons', {
                                            spinner: loading == 'remove_shopping_cart',
                                        }) }, { children: load_icon('remove_shopping_cart') })), ' ', delete_button_text] })), currentShareable && (_jsxs("button", Object.assign({ className: clsx('button-secondary', {
                                    disabled: loading,
                                }), type: 'submit', disabled: !!loading, name: 'checkout', onClick: linkCopyHandler }, { children: [_jsx("div", Object.assign({ className: clsx('material-icons', {
                                            spinner: loading == 'share',
                                        }) }, { children: load_icon('file_copy', loading == 'share') })), ' ', copy_link_text] }))), _jsxs("button", Object.assign({ className: clsx('button-secondary', {
                                    disabled: loading,
                                }), type: 'submit', disabled: !!loading, name: 'checkout', onClick: shareCartHandler }, { children: [_jsx("div", Object.assign({ className: clsx('material-icons', {
                                            spinner: loading == 'share',
                                        }) }, { children: load_icon(currentShareable ? 'close' : 'share', loading == 'share') })), ' ', share_text] })), _jsxs("button", Object.assign({ className: clsx('button-secondary', {
                                    disabled: loading,
                                }), type: 'submit', disabled: !!loading, name: 'checkout', onClick: printCartHandler }, { children: [_jsx("div", Object.assign({ className: clsx('material-icons') }, { children: load_icon('print') })), " ", print_text] }))] }))] }))] })));
}
export default function LoadSavedCarts(element) {
    return __awaiter(this, void 0, void 0, function* () {
        const sbElement = jQuery('<div id="sb-saved-carts"></div>');
        element.parent().append(sbElement);
        const rootDom = createRoot(sbElement[0]);
        const axios = yield ImportAxios();
        rootDom.render(_jsx(SavedCarts, Object.assign({}, globalThis.cartSaverConfig, { axios: axios })));
    });
}
